/**
 * DLC_12 — komunikacja z API narzędzi PDF.
 *
 * Jeden kafelek = jedno wywołanie `apply`. Backend sam decyduje, czy policzy to
 * od razu, czy odda zadanie w tle — dlatego `useApplyTool` zwraca albo gotowy
 * wynik, albo identyfikator zadania do odpytania.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import apiClient from "@/api/client";
import { saveBlobResponse } from "@/utils/download";
import type {
  PdfApplyResult,
  PdfChatInfo,
  PdfDocument,
  PdfOfficeOutline,
  PdfSession,
  PdfSessionDetail,
  PdfTaskStatus,
  PdfTextRuns,
  PdfPageObjects,
  PdfToolCatalog,
  PdfVersion,
} from "../types";

const BASE = "/pdf";

export const pdfKeys = {
  tools: ["pdf", "tools"] as const,
  sessions: (archived: boolean) => ["pdf", "sessions", archived] as const,
  session: (id: string | null) => ["pdf", "session", id] as const,
  versions: (id: string) => ["pdf", "versions", id] as const,
  ticket: (id: string | null) => ["pdf", "ticket", id] as const,
  chat: (id: string | null) => ["pdf", "chat", id] as const,
};

type Envelope<T> = { data: T; errors: unknown; meta: Record<string, unknown> };

/** Komunikat błędu z koperty { data, errors, meta } — bez „Request failed”. */
export function pdfErrorText(error: unknown, fallback = "Nie udało się wykonać operacji."): string {
  const payload = (error as { response?: { data?: { errors?: unknown; detail?: unknown } } })
    ?.response?.data;
  const raw = payload?.errors ?? payload?.detail;
  if (typeof raw === "string" && raw.trim()) return raw;
  if (Array.isArray(raw) && typeof raw[0] === "string") return raw[0];
  if (raw && typeof raw === "object") {
    const first = Object.values(raw as Record<string, unknown>)[0];
    if (typeof first === "string") return first;
    if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  }
  return fallback;
}

export function useToolCatalog() {
  return useQuery({
    queryKey: pdfKeys.tools,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfToolCatalog>>(`${BASE}/tools/`);
      return r.data.data;
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function usePdfSessions(archived = false) {
  return useQuery({
    queryKey: pdfKeys.sessions(archived),
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfSession[]>>(
        `${BASE}/sessions/${archived ? "?archived=1" : ""}`,
      );
      return r.data.data;
    },
    staleTime: 10_000,
  });
}

export function usePdfSession(id: string | null) {
  return useQuery({
    queryKey: pdfKeys.session(id),
    enabled: !!id,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfSessionDetail>>(`${BASE}/sessions/${id}/`);
      return r.data.data;
    },
  });
}

export function invalidateSession(qc: QueryClient, id: string | null) {
  qc.invalidateQueries({ queryKey: pdfKeys.session(id) });
  qc.invalidateQueries({ queryKey: ["pdf", "sessions"] });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (title?: string) => {
      const r = await apiClient.post<Envelope<PdfSessionDetail>>(`${BASE}/sessions/`, {
        title: title ?? "",
      });
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pdf", "sessions"] }),
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; title?: string; archived?: boolean }) => {
      const { id, ...body } = vars;
      const r = await apiClient.patch<Envelope<PdfSessionDetail>>(`${BASE}/sessions/${id}/`, body);
      return r.data.data;
    },
    onSuccess: (data) => invalidateSession(qc, data.id),
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/sessions/${id}/`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pdf", "sessions"] }),
  });
}

export function useUploadDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { sessionId: string; files: File[] }) => {
      const form = new FormData();
      vars.files.forEach((f) => form.append("files", f));
      const r = await apiClient.post<Envelope<PdfDocument[]>>(
        `${BASE}/sessions/${vars.sessionId}/documents/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data.data;
    },
    onSuccess: (_d, vars) => invalidateSession(qc, vars.sessionId),
  });
}

export function useApplyTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      sessionId: string;
      operation: string;
      documents: string[];
      params?: Record<string, unknown>;
    }) => {
      const r = await apiClient.post<Envelope<PdfApplyResult>>(
        `${BASE}/sessions/${vars.sessionId}/apply/`,
        { operation: vars.operation, documents: vars.documents, params: vars.params ?? {} },
      );
      return r.data.data;
    },
    onSuccess: (_d, vars) => invalidateSession(qc, vars.sessionId),
  });
}

/** Odpytywanie ciężkiej operacji policzonej w tle. */
export function useTaskStatus(taskId: string | null, onDone?: (t: PdfTaskStatus) => void) {
  return useQuery({
    queryKey: ["pdf", "task", taskId],
    enabled: !!taskId,
    refetchInterval: (q) => {
      const s = (q.state.data as PdfTaskStatus | undefined)?.status;
      return s === "success" || s === "failure" || s === "cancelled" ? false : 1500;
    },
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfTaskStatus>>(`${BASE}/tasks/${taskId}/`);
      const data = r.data.data;
      if (["success", "failure", "cancelled"].includes(data.status)) onDone?.(data);
      return data;
    },
  });
}

/** Ciężkie operacje liczone w tle dla tej sprawy — także te zlecone przez
 * asystenta, których pulpit nie zna z odpowiedzi na własne kliknięcie. */
export function useSessionTasks(sessionId: string | null) {
  return useQuery({
    queryKey: ["pdf", "session-tasks", sessionId],
    enabled: !!sessionId,
    refetchInterval: (q) => {
      const rows = (q.state.data as PdfTaskStatus[] | undefined) ?? [];
      return rows.some((t) => t.status === "pending" || t.status === "running") ? 1500 : 6000;
    },
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfTaskStatus[]>>(
        `${BASE}/sessions/${sessionId}/tasks/`,
      );
      return r.data.data;
    },
  });
}

export function useDocumentVersions(documentId: string | null) {
  return useQuery({
    queryKey: pdfKeys.versions(documentId ?? ""),
    enabled: !!documentId,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfVersion[]>>(
        `${BASE}/documents/${documentId}/versions/`,
      );
      return r.data.data;
    },
  });
}

export function useRestoreVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { documentId: string; number: number; sessionId: string }) => {
      const r = await apiClient.post<Envelope<PdfDocument>>(
        `${BASE}/documents/${vars.documentId}/restore/${vars.number}/`,
      );
      return r.data.data;
    },
    onSuccess: (_d, vars) => {
      invalidateSession(qc, vars.sessionId);
      qc.invalidateQueries({ queryKey: pdfKeys.versions(vars.documentId) });
    },
  });
}

export function useRenameDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { documentId: string; name: string; sessionId: string }) => {
      const r = await apiClient.patch<Envelope<PdfDocument>>(
        `${BASE}/documents/${vars.documentId}/`,
        { name: vars.name },
      );
      return r.data.data;
    },
    onSuccess: (_d, vars) => invalidateSession(qc, vars.sessionId),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { documentId: string; sessionId: string }) => {
      await apiClient.delete(`${BASE}/documents/${vars.documentId}/`);
      return vars;
    },
    onSuccess: (vars) => invalidateSession(qc, vars.sessionId),
  });
}

/**
 * Bilet do podglądu. `<iframe>` i `<img>` nie dodadzą nagłówka z tokenem, więc
 * adres niesie krótko żyjący podpis. Odświeżamy go z zapasem przed wygaśnięciem.
 */
export function useDocumentTicket(documentId: string | null) {
  return useQuery({
    queryKey: pdfKeys.ticket(documentId),
    enabled: !!documentId,
    staleTime: 8 * 60 * 1000,
    refetchInterval: 8 * 60 * 1000,
    queryFn: async () => {
      const r = await apiClient.post<Envelope<{ bilet: string; expires_in: number }>>(
        `${BASE}/documents/${documentId}/ticket/`,
      );
      return r.data.data.bilet;
    },
  });
}

export function previewUrl(documentId: string, ticket: string): string {
  return `/api/v1${BASE}/view/${documentId}/?bilet=${encodeURIComponent(ticket)}`;
}

export function pageImageUrl(
  documentId: string,
  page: number,
  ticket: string,
  width = 220,
  version = 0,
): string {
  return `/api/v1${BASE}/view/${documentId}/page/${page}/?w=${width}&bilet=${encodeURIComponent(
    ticket,
  )}&v=${version}`;
}

/**
 * Pobranie pliku na dysk.
 *
 * NIE robimy tego zwykłym `<a href>`: nawigacja przeglądarki nie niesie nagłówka
 * z tokenem, więc użytkownik zamiast pliku dostawał stronę z „Nie podano danych
 * uwierzytelniających” (zgłoszenie 2026-08-29). Plik ciągniemy klientem API, który
 * token ma, i zapisujemy pod nazwą podaną przez serwer.
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (doc: { id: string; name: string }) => {
      const r = await apiClient.get(`${BASE}/documents/${doc.id}/download/`, {
        responseType: "blob",
      });
      saveBlobResponse(r, doc.name);
    },
  });
}

/** Edytowalne linie tekstu strony (warstwa wektorowa albo OCR ze skanu). */
/** `version` w kluczu: po zapisie treść strony jest inna, a identyfikatory linii
 * biorą się z numerów operatorów w potoku — stara lista wskazywałaby nieistniejące
 * napisy. Nowa wersja pliku = nowe zapytanie. */
export function usePageTextRuns(
  documentId: string | null, page: number, enabled: boolean, version?: number,
) {
  return useQuery({
    queryKey: ["pdf", "text-runs", documentId, page, version ?? 0],
    enabled: !!documentId && enabled,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfTextRuns>>(
        `${BASE}/documents/${documentId}/text-runs/${page}/`,
      );
      return r.data.data;
    },
  });
}

/** Zawartość strony, która NIE jest tekstem: obrazy i rysunki wektorowe.
 * Ten sam układ współrzędnych co linie tekstu (ułamki od lewego górnego rogu). */
export function usePageObjects(
  documentId: string | null, page: number, enabled: boolean, version?: number,
) {
  return useQuery({
    queryKey: ["pdf", "objects", documentId, page, version ?? 0],
    enabled: !!documentId && enabled,
    retry: false,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfPageObjects>>(
        `${BASE}/documents/${documentId}/objects/${page}/`,
      );
      return r.data.data;
    },
  });
}

/**
 * Konspekt pliku biurowego: akapity ze stylami, tabele i arkusze.
 *
 * `version` w kluczu — dokładnie z tego samego powodu, co przy liniach tekstu:
 * identyfikatory bloków to POZYCJE w dokumencie, więc po dopisaniu akapitu stary
 * konspekt wskazywałby przesunięte miejsca. Nowa wersja pliku = nowe zapytanie.
 */
export function useDocumentOutline(documentId: string | null, version: number, enabled = true) {
  return useQuery({
    queryKey: ["pdf", "outline", documentId, version],
    enabled: !!documentId && enabled,
    retry: false,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfOfficeOutline>>(
        `${BASE}/documents/${documentId}/outline/`,
      );
      return r.data.data;
    },
  });
}

export function usePdfChatInfo(sessionId: string | null) {
  return useQuery({
    queryKey: pdfKeys.chat(sessionId),
    enabled: !!sessionId,
    queryFn: async () => {
      const r = await apiClient.get<Envelope<PdfChatInfo>>(`${BASE}/sessions/${sessionId}/chat/`);
      return r.data.data;
    },
  });
}

export function useEnsurePdfChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const r = await apiClient.post<Envelope<PdfChatInfo>>(
        `${BASE}/sessions/${sessionId}/chat/`,
      );
      return r.data.data;
    },
    onSuccess: (data) => qc.setQueryData(pdfKeys.chat(data.session_id), data),
  });
}

export function useSetCompanyData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { sessionId: string; enabled: boolean }) => {
      const r = await apiClient.post<Envelope<PdfChatInfo>>(
        `${BASE}/sessions/${vars.sessionId}/company-data/`,
        { enabled: vars.enabled },
      );
      return r.data.data;
    },
    onSuccess: (data) => qc.setQueryData(pdfKeys.chat(data.session_id), data),
  });
}
